import * as React from 'react';

/**
 * Measure a DOM element and keep the measurement current as it resizes.
 *
 * Returns the latest measurement plus a callback ref to attach to the element.
 * A callback ref (not `useRef` + `useEffect`) because callers here render the
 * measured node conditionally — behind a loading branch, or only once a table
 * overflows — so a mount-only effect would run while the node is still absent
 * and never measure it once it appears.
 *
 * The observer is torn down and recreated in step with the DOM, and the value
 * resets to 0 when the node unmounts.
 */
export function useMeasuredElement<T extends HTMLElement>(
  measure: (element: T) => number,
): [number, (node: T | null) => void] {
  const [value, setValue] = React.useState(0);
  const observerRef = React.useRef<ResizeObserver | null>(null);
  const measureRef = React.useRef(measure);
  measureRef.current = measure;

  const ref = React.useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!node) {
      setValue(0);
      return;
    }

    const sync = () => setValue(measureRef.current(node));
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return [value, ref];
}
